import { useRef } from 'react';
import { useSlideDeckPdf } from './useSlideDeckPdf';
import { ClassroomDeckMetrics } from './classroomReportDeck';
import styles from './SchoolReportPDFSlides.module.scss';
import iconActivities from '../../images/schoolReportDeck/icon-activities.png';
import iconStories from '../../images/schoolReportDeck/icon-stories.png';
import {
  ELEMENTARY_MOOD_COLORS,
  ELEMENTARY_MOOD_TEXT_COLORS,
  ELEMENTARY_STACKED_COLORS,
  ELEMENTARY_STACKED_ORDER,
  GAME_BAR_COLORS,
  HorizontalBarChart,
  MoodDirection,
  MoodStackedBarChart,
  SECONDARY_MOOD_COLORS,
  SECONDARY_MOOD_TEXT_COLORS,
  SECONDARY_STACKED_COLORS,
  SECONDARY_STACKED_ORDER,
  STORY_BAR_COLORS,
  VerticalBarChart,
  pluralize,
} from './SchoolReportPDFSlides';

interface DeckProps {
  metrics: ClassroomDeckMetrics;
  fileName: string;
  onComplete: () => void;
}

const ClassroomReportPDFSlides = ({ metrics, fileName, onComplete }: DeckProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useSlideDeckPdf(containerRef, fileName, onComplete, 'classroom report');

  const {
    title,
    dateRangeLabel,
    studentLoginsTotal,
    studentLoginsByMonth,
    elementaryMoods,
    elementaryUpMoodPercent,
    elementaryStudents,
    elementaryMoodsByMonth,
    secondaryMoods,
    secondaryUpMoodPercent,
    secondaryStudents,
    secondaryMoodsByMonth,
    topGames,
    activitiesCount,
    topStories,
    storiesCount,
  } = metrics;

  // A single-line title has to fit a fixed-width slide regardless of how long the classroom name is,
  // so scale the font down for longer names instead of letting it overflow or wrap unpredictably.
  const fitTitleFontSize = (text: string, base: number) => {
    const ratio = text.length > 28 ? 0.485 : text.length > 20 ? 0.6 : text.length > 13 ? 0.75 : 1;
    return Math.round(base * ratio);
  };
  const coverTitleFontSize = fitTitleFontSize(title, 107);

  const elementaryChipStops = [115, 212, 309, 406, 503, 600];
  const secondaryChipStops = [91, 179, 266, 354, 441, 529, 616];

  return (
    <div ref={containerRef} className={styles.pdfContainer}>
      <div className={styles.slide} data-deck-slide>
        <div className={styles.coverBand} />
        <div className={styles.coverContent}>
          <h1 className={styles.coverTitle} style={{ fontSize: coverTitleFontSize }}>
            {title}
          </h1>
        </div>
        <div className={styles.coverDate}>{dateRangeLabel}</div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.blueSlideTitle}>Student Engagement - {title}</div>
        <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
        <div className={styles.chartImageArea}>
          <VerticalBarChart
            items={studentLoginsByMonth.map((item) => ({ label: item.month, value: item.logins }))}
            color='#fb542b'
            showGrid
          />
        </div>
        <div className={styles.totalBox}>
          <div className={styles.totalBoxLabel}>Total:</div>
          <div className={styles.totalBoxValue}>{studentLoginsTotal}</div>
        </div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.blueSlideTitle} style={{ top: 69 }}>
          Feelings Check-in - Elementary
        </div>
        <div className={styles.moodPeriod}>{dateRangeLabel}</div>
        <div className={styles.moodChartArea}>
          <MoodStackedBarChart
            series={elementaryMoodsByMonth}
            order={ELEMENTARY_STACKED_ORDER}
            colors={ELEMENTARY_STACKED_COLORS}
          />
        </div>
        <p className={styles.moodIntro} style={{ left: 1030, top: 302 }}>
          The <MoodDirection percent={elementaryUpMoodPercent} /> for K-5 is representative of{' '}
          {pluralize(elementaryStudents, 'student')}
        </p>
        {elementaryMoods.map((item, index) => (
          <div
            key={item.mood}
            className={styles.moodChip}
            style={{
              left: 869,
              top: elementaryChipStops[index],
              width: 155,
              height: 90,
              backgroundColor: ELEMENTARY_MOOD_COLORS[item.mood],
              color: ELEMENTARY_MOOD_TEXT_COLORS[item.mood],
            }}
          >
            <span className={styles.moodChipPercent} style={{ fontSize: 28 }}>
              {item.percent}%
            </span>
            <span className={styles.moodChipName} style={{ fontSize: 21 }}>
              {item.mood}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.blueSlideTitle} style={{ top: 69 }}>
          Feelings Check-in - Secondary
        </div>
        <div className={styles.moodPeriod}>{dateRangeLabel}</div>
        <div className={styles.moodChartArea}>
          <MoodStackedBarChart
            series={secondaryMoodsByMonth}
            order={SECONDARY_STACKED_ORDER}
            colors={SECONDARY_STACKED_COLORS}
          />
        </div>
        <p className={styles.moodIntro} style={{ left: 1015, top: 301 }}>
          The <MoodDirection percent={secondaryUpMoodPercent} /> for 6-12 is representative of{' '}
          {pluralize(secondaryStudents, 'student')}
        </p>
        {secondaryMoods.map((item, index) => (
          <div
            key={item.mood}
            className={styles.moodChip}
            style={{
              left: 883,
              top: secondaryChipStops[index],
              width: 120,
              height: 79,
              backgroundColor: SECONDARY_MOOD_COLORS[item.mood],
              color: SECONDARY_MOOD_TEXT_COLORS[item.mood],
            }}
          >
            <span className={styles.moodChipPercent} style={{ fontSize: 27 }}>
              {item.percent}%
            </span>
            <span className={styles.moodChipName} style={{ fontSize: 20 }}>
              {item.mood}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.slideTitle}>Most popular games</div>
        <div className={styles.slideSubtitle}>{dateRangeLabel}</div>
        <div className={styles.chartImageArea}>
          <HorizontalBarChart
            items={topGames.map((item) => ({ label: item.name, value: item.plays }))}
            colors={GAME_BAR_COLORS}
          />
        </div>
        <img className={styles.statIcon} src={iconActivities} alt='' style={{ left: 1004, top: 520 }} />
        <div className={styles.gamesText}>
          <strong className={styles.statStrong}>{activitiesCount}</strong> games played
        </div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.slideTitle}>Most popular stories</div>
        <div className={styles.slideSubtitle}>{dateRangeLabel}</div>
        <div className={styles.chartImageArea}>
          <HorizontalBarChart
            items={topStories.map((item) => ({ label: item.name, value: item.reads }))}
            colors={STORY_BAR_COLORS}
          />
        </div>
        <img className={styles.statIcon} src={iconStories} alt='' style={{ left: 1004, top: 520 }} />
        <div className={styles.statText}>
          <strong className={styles.statStrong}>{storiesCount}</strong> stories read
        </div>
      </div>
    </div>
  );
};

export default ClassroomReportPDFSlides;
